import { useI18n } from '../I18n';

import { useEffect, useRef, useState } from 'react';

/**
 * Returns the screen-reader announcement string for a suggestions list
 * (WCAG 4.1.3). Pair with a `<LiveRegion>` (e.g. via the
 * `<SuggestionsAnnouncement>` component) so the text is rendered into the
 * platform's live region — `aria-live` on web, `accessibilityLiveRegion`
 * on Android, the iOS equivalent on iOS — instead of using the imperative
 * `AccessibilityInfo.announce*` APIs and the timing/queueing pitfalls
 * that came with them (RN-2561, RN-2562, RN-3011).
 *
 * Gating semantics:
 * - Announces ONCE per "appearance" (0 -> N) of the suggestions list.
 * - Holds the same text while the count fluctuates (N -> M) so screen
 *   readers don't re-announce on every keystroke. `LiveRegion` only
 *   re-announces when the rendered text actually changes.
 * - Resets when suggestions empty (N -> 0) so the next appearance announces.
 *
 * To suppress announcements (e.g. "don't announce while user is mid-selection"),
 * pass an empty array — the call site owns that decision.
 */
export function useSuggestionsAnnouncement(suggestions: unknown[]): string {
    const i18n = useI18n();
    const hasAnnouncedRef = useRef(false);
    const [announcement, setAnnouncement] = useState('');

    useEffect(() => {
        if (suggestions.length === 0) {
            hasAnnouncedRef.current = false;
            setAnnouncement('');
            return;
        }

        if (!hasAnnouncedRef.current) {
            hasAnnouncedRef.current = true;
            const count = suggestions.length;
            setAnnouncement(
                i18n.ngettext(
                    '{0} suggestion available',
                    '{0} suggestions available',
                    count,
                    [String(count)],
                ),
            );
        }
    }, [suggestions, i18n]);

    return announcement;
}
